import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  Typography, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  InputAdornment, Box, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PaletteIcon from '@mui/icons-material/Palette';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useFavicon } from '../useFavicon';
import linksApi from '../api/linksApi';
import appInsights from '../services/appInsights';
import QRCodeCustomizer from '../components/QRCodeCustomizer';

const LINK_FAVICON_URL = process.env.PUBLIC_URL + "/link.ico"; // Make sure link.ico is in your public/ folder

// Create component-specific logger for AddLinksPage
const logger = appInsights.getComponentLogger('AddLinksPage');

/**
 * ResizableHeader component for table columns with sorting and resizing capabilities
 */
const ResizableHeader = ({ 
  children, 
  columnKey, 
  width, 
  onResize, 
  sortable = false, 
  sortField, 
  sortDirection,
  onSort 
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(width);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    e.preventDefault();
    
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + deltaX); // Minimum 50px width
    onResize(columnKey, newWidth);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add event listeners for mouse move and up
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, startX, startWidth, width, onResize, columnKey]);

  const getSortIndicator = () => {
    if (!sortable || !sortDirection) return '';
    
    // Check if this column is currently being sorted
    const currentSortField = sortField;
    let isCurrentSortColumn = false;
    
    if (columnKey === 'name' && currentSortField === 'name') isCurrentSortColumn = true;
    else if (columnKey === 'url' && currentSortField === 'url') isCurrentSortColumn = true;
    else if (columnKey === 'createdAt' && currentSortField === 'createdAt') isCurrentSortColumn = true;
    else if (columnKey === 'updatedAt' && currentSortField === 'updatedAt') isCurrentSortColumn = true;
    
    if (!isCurrentSortColumn) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const handleHeaderClick = () => {
    if (sortable && onSort) {
      onSort(sortField || columnKey);
    }
  };

  return (
    <TableCell 
      sx={{ 
        fontWeight: 'bold', 
        border: '1px solid #ddd',
        width: `${width}px`,
        minWidth: `${width}px`,
        position: 'relative',
        padding: '8px',
        userSelect: 'none',
        cursor: sortable ? 'pointer' : 'default'
      }}
      onClick={handleHeaderClick}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        height: '100%',
        color: 'white'
      }}>
        <span>{children}{getSortIndicator()}</span>
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? '#1976d2' : 'transparent',
            borderRight: '2px solid transparent'
          }}
          onMouseDown={handleMouseDown}
          className="resize-handle"
        />
      </div>
    </TableCell>
  );
};

const AddLinksPage = () => {
  useFavicon(LINK_FAVICON_URL); // Set the favicon for this page
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [links, setLinks] = useState(null);  // Changed to null to distinguish between initial and empty states
  const [isLoading, setIsLoading] = useState(true);
  const [urlError, setUrlError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLinkId, setEditLinkId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [displayedQRCodes, setDisplayedQRCodes] = useState(new Set()); // Track which QR codes are currently displayed
  const [qrCodeDataUrls, setQRCodeDataUrls] = useState({}); // Cache QR code data URLs
  const [loadingQRCodes, setLoadingQRCodes] = useState(new Set()); // Track which QR codes are currently loading
  const [successMessage, setSuccessMessage] = useState(''); // Success message for QR operations
  
  // QR Code Customizer state
  const [qrCustomizerOpen, setQRCustomizerOpen] = useState(false);
  const [customizingLinkId, setCustomizingLinkId] = useState(null);
  const [customizingLinkName, setCustomizingLinkName] = useState('');
  
  // New state for sorting and search
  const [sortBy, setSortBy] = useState('createdAt'); // Sort by: 'name', 'url', 'createdAt'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [searchQuery, setSearchQuery] = useState(''); // Search query

  // Column widths for resizing
  const [columnWidths, setColumnWidths] = useState({
    sn: 80,
    name: 200,
    url: 300,
    createdAt: 180,
    updatedAt: 180,
    actions: 240  // Increased to accommodate custom QR button
  });

  // Helper function to normalize link IDs to ensure consistent string format
  const normalizeId = (id) => {
    if (!id) return '';
    if (typeof id === 'object' && id !== null && typeof id.toString === 'function') {
      return id.toString();
    }
    return String(id);
  };

  useEffect(() => {
    logger.info('AddLinksPage component mounted', {
      operation: 'componentMount',
      timestamp: new Date().toISOString()
    });
    
    // Track page view
    appInsights.trackPageView({
      name: 'AddLinksPage',
      properties: {
        component: 'AddLinksPage',
        source: 'frontend',
        userAgent: navigator.userAgent.substring(0, 100)
      }
    });
    
    fetchLinks();
    
    // Cleanup function
    return () => {
      logger.info('AddLinksPage component unmounting', {
        operation: 'componentUnmount',
        timestamp: new Date().toISOString()
      });
    };
  }, []);  const fetchLinks = async () => {
    setIsLoading(true);
    logger.info('Starting to fetch links', {
      operation: 'fetchLinks',
      timestamp: new Date().toISOString()
    });
    
    try {
      console.log('%c Fetching links...', 'background: #222; color: #bada55; font-size: 16px');
      const response = await linksApi.getAll();
      console.log('Raw API response:', response);
      
      if (!response || !response.data) {
        logger.warn('No response or data from API', {
          operation: 'fetchLinks',
          hasResponse: !!response,
          hasData: !!(response && response.data)
        });
        setLinks([]);
        return;
      }

      // Ensure we're working with an array
      const linksData = Array.isArray(response.data) ? response.data : [];
      console.log('Processed links data:', linksData);
      
      logger.info('Successfully fetched links', {
        operation: 'fetchLinks',
        linksCount: linksData.length,
        timestamp: new Date().toISOString()
      });
      
      // Track successful links fetch event
      appInsights.trackEvent('LinksFetched', {
        component: 'AddLinksPage',
        source: 'frontend',
        linksCount: linksData.length,
        operation: 'fetchLinks'
      });
      
      setLinks(linksData);
    } catch (error) {
      console.error('Error fetching links:', error);
      
      logger.error('Failed to fetch links', {
        operation: 'fetchLinks',
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      
      // Track error event
      appInsights.trackException(error, {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'fetchLinks',
        errorType: 'FetchLinksError'
      });
      
      setLinks([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateUrl = (url) => {
    logger.verbose('Validating URL', {
      operation: 'validateUrl',
      urlLength: url ? url.length : 0,
      hasProtocol: url ? url.startsWith('http') : false
    });
    
    // Check if URL is empty or just whitespace
    if (!url || url.trim().length === 0) {
      return false;
    }
    
    const trimmedUrl = url.trim();
    
    // More strict URL validation regex
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // Optional protocol
      '(' + // Start of domain group
        '([a-z\\d]([a-z\\d-]*[a-z\\d])*\\.)+[a-z]{2,}' + // Domain with TLD (must have at least one dot)
        '|' + // OR
        '((\\d{1,3}\\.){3}\\d{1,3})' + // IP address
        '|' + // OR
        'localhost' + // localhost
      ')' + // End of domain group
      '(\\:\\d+)?' + // Optional port
      '(\\/[-a-z\\d%_.~+]*)*' + // Optional path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // Optional query string
      '(\\#[-a-z\\d_]*)?$', // Optional fragment
      'i'
    );
    
    // Additional checks for minimum requirements
    const hasValidDomain = /[a-z0-9.-]+\.[a-z]{2,}/i.test(trimmedUrl) || // Has TLD
                          /\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(trimmedUrl) || // Is IP
                          /localhost/i.test(trimmedUrl); // Is localhost
    
    const isValid = urlPattern.test(trimmedUrl) && hasValidDomain;
    
    logger.verbose('URL validation result', {
      operation: 'validateUrl',
      isValid: isValid,
      hasValidDomain: hasValidDomain,
      url: trimmedUrl ? trimmedUrl.substring(0, 50) + '...' : 'empty' // Log partial URL for privacy
    });
    
    return isValid;
  };  const handleAddLink = async (e) => {
    e.preventDefault();
    
    logger.info('User attempting to add new link', {
      operation: 'addLink',
      hasName: !!name,
      hasUrl: !!url,
      nameLength: name ? name.length : 0,
      urlLength: url ? url.length : 0,
      timestamp: new Date().toISOString()
    });
    
    if (!validateUrl(url)) {
      setUrlError('Invalid URL format');
      
      logger.warn('URL validation failed during add link', {
        operation: 'addLink',
        error: 'Invalid URL format',
        urlLength: url ? url.length : 0
      });
      
      // Track validation error event
      appInsights.trackEvent('LinkValidationFailed', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'addLink',
        errorType: 'InvalidURL',
        urlLength: url ? url.length : 0
      });
      
      return;
    }
    setUrlError('');
    
    try {
      console.log('Creating new link:', { name, url });
      
      logger.info('Creating new link via API', {
        operation: 'addLink',
        linkName: name,
        urlDomain: url ? (url.startsWith('http') ? new URL(url).hostname : 'no-protocol') : 'unknown'
      });
      
      await linksApi.create({ name, url });
      
      logger.info('Successfully created new link', {
        operation: 'addLink',
        linkName: name,
        timestamp: new Date().toISOString()
      });
      
      // Track successful link creation
      appInsights.trackEvent('LinkCreated', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'addLink',
        linkName: name,
        urlDomain: url ? (url.startsWith('http') ? new URL(url).hostname : 'no-protocol') : 'unknown'
      });
      
      setName('');
      setUrl('');
      setSuccessMessage('Link added successfully!'); // Set success message
      setTimeout(() => setSuccessMessage(''), 3000); // Clear message after 3 seconds
      await fetchLinks(); // Refresh the links after adding a new one
    } catch (error) {
      console.error('Error adding link:', error);
      
      logger.error('Failed to create new link', {
        operation: 'addLink',
        error: error.message,
        stack: error.stack,
        linkName: name,
        timestamp: new Date().toISOString()
      });
      
      // Track error event
      appInsights.trackException(error, {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'addLink',
        errorType: 'CreateLinkError',
        linkName: name
      });
    }
  };  const handleDeleteLink = async (id) => {
    logger.info('User attempting to delete link', {
      operation: 'deleteLink',
      linkId: id,
      timestamp: new Date().toISOString()
    });
    
    try {
      console.log('Deleting link with id:', id);
      
      await linksApi.delete(id);
      
      logger.info('Successfully deleted link', {
        operation: 'deleteLink',
        linkId: id,
        timestamp: new Date().toISOString()
      });
      
      // Track successful link deletion
      appInsights.trackEvent('LinkDeleted', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'deleteLink',
        linkId: id
      });
      
      setSuccessMessage('Link deleted successfully!'); // Set success message
      setTimeout(() => setSuccessMessage(''), 3000); // Clear message after 3 seconds
      
      await fetchLinks(); // Refresh the links after deleting one
    } catch (error) {
      console.error('Error deleting link:', error);
      
      logger.error('Failed to delete link', {
        operation: 'deleteLink',
        linkId: id,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      
      // Track error event
      appInsights.trackException(error, {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'deleteLink',
        errorType: 'DeleteLinkError',
        linkId: id
      });
    }
  };

  const handleEditLink = (link) => {
    const linkId = normalizeId(link._id);
    logger.info('User opening edit dialog for link', {
      operation: 'editLinkDialog',
      linkId: linkId,
      linkName: link.name,
      timestamp: new Date().toISOString()
    });
    
    // Track edit dialog open event
    appInsights.trackEvent('EditLinkDialogOpened', {
      component: 'AddLinksPage',
      source: 'frontend',
      operation: 'editLinkDialog',
      linkId: linkId,
      linkName: link.name
    });
    
    setEditLinkId(link._id);
    setEditName(link.name);
    setEditUrl(link.url);
    setEditDialogOpen(true);
  };  const handleUpdateLink = async () => {
    logger.info('User attempting to update link', {
      operation: 'updateLink',
      linkId: editLinkId,
      hasName: !!editName,
      hasUrl: !!editUrl,
      nameLength: editName ? editName.length : 0,
      urlLength: editUrl ? editUrl.length : 0,
      timestamp: new Date().toISOString()
    });
    
    if (!validateUrl(editUrl)) {
      setUrlError('Invalid URL format');
      
      logger.warn('URL validation failed during update link', {
        operation: 'updateLink',
        linkId: editLinkId,
        error: 'Invalid URL format',
        urlLength: editUrl ? editUrl.length : 0
      });
      
      // Track validation error event
      appInsights.trackEvent('LinkUpdateValidationFailed', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'updateLink',
        errorType: 'InvalidURL',
        linkId: editLinkId,
        urlLength: editUrl ? editUrl.length : 0
      });
      
      return;
    }
    setUrlError('');
    
    try {
      console.log('Updating link:', { id: editLinkId, name: editName, url: editUrl });
      
      logger.info('Updating link via API', {
        operation: 'updateLink',
        linkId: editLinkId,
        linkName: editName,
        urlDomain: editUrl ? (editUrl.startsWith('http') ? new URL(editUrl).hostname : 'no-protocol') : 'unknown'
      });
      
      await linksApi.update(editLinkId, { name: editName, url: editUrl });
      
      logger.info('Successfully updated link', {
        operation: 'updateLink',
        linkId: editLinkId,
        linkName: editName,
        timestamp: new Date().toISOString()
      });
      
      // Track successful link update
      appInsights.trackEvent('LinkUpdated', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'updateLink',
        linkId: editLinkId,
        linkName: editName,
        urlDomain: editUrl ? (editUrl.startsWith('http') ? new URL(editUrl).hostname : 'no-protocol') : 'unknown'
      });
      
      setEditDialogOpen(false);
      setSuccessMessage('Link updated successfully!'); // Set success message
      setTimeout(() => setSuccessMessage(''), 3000); // Clear message after 3 seconds
      await fetchLinks(); // Refresh the links after updating one
    } catch (error) {
      console.error('Error updating link:', error);
      
      logger.error('Failed to update link', {
        operation: 'updateLink',
        linkId: editLinkId,
        error: error.message,
        stack: error.stack,
        linkName: editName,
        timestamp: new Date().toISOString()
      });
      
      // Track error event
      appInsights.trackException(error, {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'updateLink',
        errorType: 'UpdateLinkError',
        linkId: editLinkId,
        linkName: editName
      });
    }
  };

  // QR Code handlers
  const handleDownloadQRCode = async (linkId, linkName) => {
    // Make sure the ID is properly normalized - declared outside try block
    // so it's available in the catch block
    const normalizedId = normalizeId(linkId);
    // Variable to track if we're using a custom QR code
    let hasCustomQR = false;
    
    try {
      // Debug: log the exact link data being passed
      console.log('=== QR Code Download Debug ===');
      console.log('Original linkId:', linkId);
      console.log('Original linkId type:', typeof linkId);
      console.log('Normalized linkId:', normalizedId);
      console.log('Normalized linkId length:', normalizedId.length);
      console.log('linkName:', linkName);
      
      // Check if we have a custom QR code for this link
      hasCustomQR = qrCodeDataUrls && qrCodeDataUrls[normalizedId]?.dataUrl;
      console.log('Has custom QR?', !!hasCustomQR);
      console.log('==============================');
      
      logger.info('User downloading QR code for link', {
        operation: 'downloadQRCode',
        linkId: normalizedId,
        linkName: linkName,
        useCustom: !!hasCustomQR,
        timestamp: new Date().toISOString()
      });

      if (hasCustomQR) {
        // Use custom QR code if available
        const customData = qrCodeDataUrls[normalizedId];
        await linksApi.downloadCustomQRCode(
          customData.dataUrl,
          linkName,
          customData.customOptions?.format || 'png'
        );
        logger.info('Custom QR code downloaded successfully', {
          operation: 'downloadCustomQRCode',
          linkId: normalizedId,
          linkName: linkName,
          timestamp: new Date().toISOString()
        });
      } else {
        // Fall back to default QR code
        await linksApi.downloadQRCode(normalizedId, linkName);
        logger.info('Default QR code downloaded successfully', {
          operation: 'downloadQRCode',
          linkId: normalizedId,
          linkName: linkName,
          timestamp: new Date().toISOString()
        });
      }

      // Track successful download
      appInsights.trackEvent('LinkQRCodeDownloaded', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: hasCustomQR ? 'downloadCustomQRCode' : 'downloadQRCode',
        linkId: normalizedId,
        linkName: linkName,
        isCustom: !!hasCustomQR
      });

      setSuccessMessage('QR code downloaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      logger.error('Error downloading QR code for link', {
        operation: hasCustomQR ? 'downloadCustomQRCode' : 'downloadQRCode',
        linkId: normalizedId,
        linkName: linkName,
        isCustom: !!hasCustomQR,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      // Track error
      appInsights.trackException(error, {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'downloadQRCode',
        linkId: linkId,
        linkName: linkName
      });

      alert(`Error downloading QR code: ${error.message}`);
    }
  };

  const handleToggleQRCode = async (linkId) => {
    // Make sure we're using a normalized ID consistently throughout this function
    const normalizedId = normalizeId(linkId);
    
    if (displayedQRCodes.has(normalizedId)) {
      // Hide QR code
      setDisplayedQRCodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(normalizedId);
        return newSet;
      });
      
      // Remove from cache
      setQRCodeDataUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[normalizedId];
        return newUrls;
      });

      logger.info('User hid QR code for link', {
        operation: 'hideQRCode',
        linkId: normalizedId,
        timestamp: new Date().toISOString()
      });
    } else {
      // Show QR code
      if (!qrCodeDataUrls[normalizedId] && !loadingQRCodes.has(normalizedId)) {
        try {
          setLoadingQRCodes(prev => new Set(prev).add(normalizedId));
          
          logger.info('User requesting QR code display for link', {
            operation: 'showQRCode',
            linkId: normalizedId,
            timestamp: new Date().toISOString()
          });

          const qrCodeData = await linksApi.getQRCodeDataURL(normalizedId);
          
          setQRCodeDataUrls(prev => ({
            ...prev,
            [normalizedId]: qrCodeData
          }));

          logger.info('QR code data loaded successfully for link', {
            operation: 'showQRCode',
            linkId: normalizedId,
            timestamp: new Date().toISOString()
          });

          // Track successful QR code display
          appInsights.trackEvent('LinkQRCodeDisplayed', {
            component: 'AddLinksPage',
            source: 'frontend',
            operation: 'showQRCode',
            linkId: normalizedId
          });
        } catch (error) {
          logger.error('Error loading QR code for link', {
            operation: 'showQRCode',
            linkId: normalizedId,
            error: error.message,
            timestamp: new Date().toISOString()
          });

          // Track error
          appInsights.trackException(error, {
            component: 'AddLinksPage',
            source: 'frontend',
            operation: 'showQRCode',
            linkId: linkId
          });

          alert(`Error loading QR code: ${error.message}`);
        } finally {
          setLoadingQRCodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(normalizedId);
            return newSet;
          });
        }
      }
      
      setDisplayedQRCodes(prev => new Set(prev).add(normalizedId));
    }
  };

  // Handle opening QR code customizer
  const handleOpenQRCustomizer = (linkId, linkName) => {
    const normalizedId = normalizeId(linkId);
    setCustomizingLinkId(normalizedId);
    setCustomizingLinkName(linkName);
    setQRCustomizerOpen(true);
    
    logger.info('User opened QR code customizer', {
      operation: 'openQRCustomizer',
      linkId: normalizedId,
      timestamp: new Date().toISOString()
    });

    // Track QR customizer open event
    appInsights.trackEvent('QRCustomizerOpened', {
      component: 'AddLinksPage',
      source: 'frontend',
      operation: 'openQRCustomizer',
      linkId: linkId
    });
  };

  // Handle custom QR generated
  const handleCustomQRGenerated = (customQRData) => {
    // Update the QR code cache with custom data
    setQRCodeDataUrls(prev => ({
      ...prev,
      [customizingLinkId]: customQRData
    }));

    // Show the QR code if not already displayed
    setDisplayedQRCodes(prev => new Set(prev).add(customizingLinkId));

    logger.info('Custom QR code generated successfully', {
      operation: 'customQRGenerated',
      linkId: customizingLinkId,
      timestamp: new Date().toISOString()
    });

    // Track custom QR generation event
    appInsights.trackEvent('CustomQRCodeGenerated', {
      component: 'AddLinksPage',
      source: 'frontend',
      operation: 'customQRGenerated',
      linkId: customizingLinkId
    });

    setSuccessMessage('Custom QR code generated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Handle copy permanent QR URL to clipboard
  const handleCopyPermanentUrl = async (qrRedirectUrl, linkName) => {
    try {
      await navigator.clipboard.writeText(qrRedirectUrl);
      
      logger.info('Permanent QR URL copied to clipboard', {
        operation: 'copyPermanentUrl',
        linkName: linkName,
        timestamp: new Date().toISOString()
      });
      
      // Track copy event
      appInsights.trackEvent('PermanentQRUrlCopied', {
        component: 'AddLinksPage',
        source: 'frontend',
        operation: 'copyPermanentUrl',
        linkName: linkName
      });
      
      setSuccessMessage('Permanent QR URL copied to clipboard!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      logger.error('Error copying permanent QR URL to clipboard', {
        operation: 'copyPermanentUrl',
        linkName: linkName,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = qrRedirectUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setSuccessMessage('Permanent QR URL copied to clipboard!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  // Handle navigation back to main page
  const handleBackToMain = () => {
    logger.info('User navigating back to main page', {
      operation: 'navigation',
      destination: 'mainPage',
      timestamp: new Date().toISOString()
    });
    
    // Track navigation event
    appInsights.trackEvent('NavigateToMainPage', {
      component: 'AddLinksPage',
      source: 'frontend',
      operation: 'navigation'
    });
    
    navigate('/');
  };

  // Sorting and filtering logic
  const sortedAndFilteredLinks = useMemo(() => {
    if (!links || !Array.isArray(links)) return [];
    
    // First, filter based on search query
    let filteredLinks = links.filter(link => {
      if (!searchQuery.trim()) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        link.name?.toLowerCase().includes(query) ||
        link.url?.toLowerCase().includes(query)
      );
    });
    
    // Then, sort the filtered results
    const sorted = [...filteredLinks].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'url':
          aValue = a.url?.toLowerCase() || '';
          bValue = b.url?.toLowerCase() || '';
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt || 0);
          bValue = new Date(b.updatedAt || 0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [links, searchQuery, sortBy, sortOrder]);
  
  // Handle sort change
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  
  // Handle search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Column resizing function
  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(50, newWidth) // Minimum width of 50px
    }));
  };

  // Reset column widths to default
  const resetColumnWidths = () => {
    setColumnWidths({
      sn: 80,
      name: 200,
      url: 300,
      createdAt: 180,
      updatedAt: 180,
      actions: 240  // Increased to accommodate custom QR button
    });
  };

  return (
    <div style={{ padding: '20px' }}>
      <style>
        {`
          .resize-handle {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            cursor: col-resize;
            background-color: transparent;
            transition: background-color 0.2s;
          }
          
          .resize-handle:hover {
            background-color: #1976d2 !important;
          }
          
          .resize-handle:active {
            background-color: #1565c0 !important;
          }
          
          .resizing {
            user-select: none;
            cursor: col-resize;
          }
          
          .resizable-table {
            table-layout: fixed;
            width: 100%;
          }
        `}
      </style>
      <Typography variant="h4" gutterBottom>Add Links</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        💡 Tip: Hover over column borders to resize them by dragging • Hover over table values to see full content
      </Typography>
      <Button 
        variant="outlined" 
        color="primary" 
        onClick={handleBackToMain} 
        style={{ marginBottom: '20px', marginRight: '10px' }}
      >
        Back to Main
      </Button>
      <Button 
        variant="outlined" 
        color="secondary" 
        onClick={resetColumnWidths} 
        style={{ marginBottom: '20px' }}
      >
        Reset Column Widths
      </Button>
      <form onSubmit={handleAddLink} style={{ marginBottom: '20px' }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            logger.verbose('User typing in name field', {
              operation: 'formInput',
              field: 'name',
              valueLength: e.target.value.length,
              timestamp: new Date().toISOString()
            });
          }}
          required
          style={{ marginRight: '10px' }}
        />
        <TextField
          label="URL"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            logger.verbose('User typing in URL field', {
              operation: 'formInput',
              field: 'url',
              valueLength: e.target.value.length,
              hasProtocol: e.target.value.startsWith('http'),
              timestamp: new Date().toISOString()
            });
          }}
          required
          error={!!urlError}
          helperText={urlError}
          style={{ marginRight: '10px' }}
        />
        <Button type="submit" variant="contained" color="primary">Add Link</Button>
      </form>
      
      {/* Success Message */}
      {successMessage && (
        <div style={{
          margin: '10px 0',
          padding: '10px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Search Box */}
      <Box sx={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          label="Search links"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by name or URL..."
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {sortedAndFilteredLinks.length} of {links?.length || 0} links
        </Typography>
      </Box>
      
      <TableContainer component={Paper}>
        <Table className="resizable-table" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#86aae3' }}>
              <ResizableHeader
                columnKey="sn"
                width={columnWidths.sn}
                onResize={handleColumnResize}
                sortable={false}
              >
                #
              </ResizableHeader>
              <ResizableHeader
                columnKey="name"
                width={columnWidths.name}
                onResize={handleColumnResize}
                sortable={true}
                sortField="name"
                sortDirection={sortBy === 'name' ? sortOrder : null}
                onSort={handleSort}
              >
                Name
              </ResizableHeader>
              <ResizableHeader
                columnKey="url"
                width={columnWidths.url}
                onResize={handleColumnResize}
                sortable={true}
                sortField="url"
                sortDirection={sortBy === 'url' ? sortOrder : null}
                onSort={handleSort}
              >
                URL
              </ResizableHeader>
              <ResizableHeader
                columnKey="createdAt"
                width={columnWidths.createdAt}
                onResize={handleColumnResize}
                sortable={true}
                sortField="createdAt"
                sortDirection={sortBy === 'createdAt' ? sortOrder : null}
                onSort={handleSort}
              >
                Created At
              </ResizableHeader>
              <ResizableHeader
                columnKey="updatedAt"
                width={columnWidths.updatedAt}
                onResize={handleColumnResize}
                sortable={true}
                sortField="updatedAt"
                sortDirection={sortBy === 'updatedAt' ? sortOrder : null}
                onSort={handleSort}
              >
                Modified At
              </ResizableHeader>
              <ResizableHeader
                columnKey="actions"
                width={columnWidths.actions}
                onResize={handleColumnResize}
                sortable={false}
              >
                Actions
              </ResizableHeader>
            </TableRow>
          </TableHead>          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Loading...</TableCell>
              </TableRow>
            ) : !links || links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No links found</TableCell>
              </TableRow>
            ) : sortedAndFilteredLinks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No links match your search</TableCell>
              </TableRow>
            ) : (
              sortedAndFilteredLinks.map((link, index) => (
                <React.Fragment key={normalizeId(link._id) || index}>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                    <TableCell sx={{ 
                      border: '1px solid #ddd',
                      width: `${columnWidths.sn}px`,
                      minWidth: `${columnWidths.sn}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ 
                      border: '1px solid #ddd',
                      width: `${columnWidths.name}px`,
                      minWidth: `${columnWidths.name}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Tooltip title={link.name || ''} arrow>
                        <span>{link.name}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ 
                      border: '1px solid #ddd',
                      width: `${columnWidths.url}px`,
                      minWidth: `${columnWidths.url}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Tooltip title={link.url || ''} arrow>
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          onClick={() => {
                            logger.info('User clicked on link', {
                              operation: 'linkClick',
                              linkId: link._id,
                              linkName: link.name,
                              urlDomain: link.url ? (link.url.startsWith('http') ? new URL(link.url).hostname : 'no-protocol') : 'unknown',
                              timestamp: new Date().toISOString()
                            });
                            
                            // Track link click event
                            appInsights.trackEvent('LinkClicked', {
                              component: 'AddLinksPage',
                              source: 'frontend',
                              operation: 'linkClick',
                              linkId: link._id,
                              linkName: link.name,
                              urlDomain: link.url ? (link.url.startsWith('http') ? new URL(link.url).hostname : 'no-protocol') : 'unknown'
                            });
                          }}
                        >
                          {link.url}
                        </a>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ 
                      border: '1px solid #ddd',
                      width: `${columnWidths.createdAt}px`,
                      minWidth: `${columnWidths.createdAt}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Tooltip title={formatDate(link.createdAt)} arrow>
                        <span>{formatDate(link.createdAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ 
                      border: '1px solid #ddd',
                      width: `${columnWidths.updatedAt}px`,
                      minWidth: `${columnWidths.updatedAt}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Tooltip title={formatDate(link.updatedAt)} arrow>
                        <span>{formatDate(link.updatedAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ 
                      border: '1px solid #ddd',
                      width: `${columnWidths.actions}px`,
                      minWidth: `${columnWidths.actions}px`,
                      overflow: 'hidden'
                    }}>
                      <Tooltip title="Edit Link" arrow>
                        <IconButton onClick={() => handleEditLink(link)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Link" arrow>
                        <IconButton onClick={() => handleDeleteLink(normalizeId(link._id))} color="secondary">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip 
                        title={loadingQRCodes.has(normalizeId(link._id)) 
                          ? 'Loading QR code...' 
                          : displayedQRCodes.has(normalizeId(link._id)) 
                            ? 'Hide QR Code' 
                            : 'Show QR Code'
                        } 
                        arrow
                      >
                        <span>
                          <IconButton 
                            onClick={() => {
                              const cleanId = normalizeId(link._id);
                              handleToggleQRCode(cleanId);
                            }} 
                            color={displayedQRCodes.has(normalizeId(link._id)) ? "default" : "primary"}
                            disabled={loadingQRCodes.has(normalizeId(link._id))}
                          >
                            <QrCodeIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download QR Code" arrow>
                        <IconButton 
                          onClick={() => {
                            // Debug: log the link object before calling handleDownloadQRCode
                            console.log('=== Link Object Debug ===');
                            console.log('Full link object:', link);
                            console.log('link._id:', link._id);
                            console.log('link._id type:', typeof link._id);
                            console.log('link._id valueOf():', link._id.valueOf());
                            console.log('link._id toString():', link._id.toString());
                            console.log('JSON.stringify(link._id):', JSON.stringify(link._id));
                            console.log('========================');
                            
                            // Ensure we pass a clean string version of the ID
                            const cleanId = normalizeId(link._id);
                            handleDownloadQRCode(cleanId, link.name);
                          }} 
                          color="primary"
                        >
                          <GetAppIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Custom QR Design" arrow>
                        <IconButton 
                          onClick={() => {
                            const cleanId = normalizeId(link._id);
                            handleOpenQRCustomizer(cleanId, link.name);
                          }} 
                          color="secondary"
                        >
                          <PaletteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {displayedQRCodes.has(normalizeId(link._id)) && qrCodeDataUrls[normalizeId(link._id)] && (
                    <TableRow className="qr-code-row">
                      <TableCell colSpan={6} className="qr-code-cell" sx={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                        <div className="qr-code-container" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                          <div className="qr-code-info" style={{ flex: 1 }}>
                            <Typography variant="h6" component="h4" gutterBottom>
                              QR Code for {link.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Scan this QR code to visit the link on any device.
                            </Typography>
                            
                            {/* Custom QR Options Display */}
                            {qrCodeDataUrls[normalizeId(link._id)].customOptions && (
                              <Box sx={{ 
                                marginTop: '8px', 
                                marginBottom: '8px',
                                padding: '8px',
                                backgroundColor: '#fff3e0',
                                borderRadius: '4px',
                                border: '1px solid #ffcc02'
                              }}>
                                <Typography variant="body2" component="strong" style={{ color: '#e65100' }}>
                                  🎨 Custom Design Applied:
                                </Typography>
                                <br />
                                <Typography variant="body2" style={{ color: '#bf360c', fontSize: '11px' }}>
                                  Theme: {qrCodeDataUrls[normalizeId(link._id)].customOptions.theme || 'Custom'} • 
                                  Size: {qrCodeDataUrls[normalizeId(link._id)].customOptions.size || 256}px • 
                                  Format: {(qrCodeDataUrls[normalizeId(link._id)].customOptions.format || 'png').toUpperCase()}
                                </Typography>
                              </Box>
                            )}
                            
                            <div className="qr-code-url" style={{ 
                              marginTop: '12px',
                              padding: '12px',
                              backgroundColor: '#e3f2fd',
                              borderRadius: '6px',
                              border: '1px solid #bbdefb',
                              fontFamily: 'monospace',
                              fontSize: '12px'
                            }}>
                              <Typography variant="body2" component="strong" style={{ color: '#1976d2' }}>
                                Original URL:
                              </Typography>
                              <br />
                              <a 
                                href={qrCodeDataUrls[normalizeId(link._id)].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#0066cc',
                                  textDecoration: 'none',
                                  wordBreak: 'break-all',
                                  display: 'inline-block',
                                  marginTop: '4px'
                                }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                {qrCodeDataUrls[normalizeId(link._id)].url}
                              </a>
                              
                              <br /><br />
                              <Typography variant="body2" component="strong" style={{ color: '#2e7d32' }}>
                                QR Code URL (Permanent):
                              </Typography>
                              <br />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '4px' }}>
                                <span style={{
                                  color: '#2e7d32',
                                  wordBreak: 'break-all',
                                  display: 'inline-block',
                                  flex: 1
                                }}>
                                  {qrCodeDataUrls[normalizeId(link._id)].qrRedirectUrl}
                                </span>
                                <Tooltip title="Copy permanent QR URL to clipboard" arrow>
                                  <IconButton 
                                    onClick={() => handleCopyPermanentUrl(
                                      qrCodeDataUrls[normalizeId(link._id)].qrRedirectUrl, 
                                      link.name
                                    )}
                                    color="primary"
                                    size="small"
                                    sx={{ 
                                      flexShrink: 0,
                                      backgroundColor: '#e8f5e8',
                                      '&:hover': {
                                        backgroundColor: '#c8e6c9'
                                      }
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              
                              <br /><br />
                              <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic' }}>
                                The QR code points to the permanent URL above, which redirects to your original URL. 
                                This allows you to change the original URL while keeping the same QR code.
                              </Typography>
                            </div>
                          </div>
                          <div className="qr-code-image" style={{ flexShrink: 0 }}>
                            <img 
                              src={qrCodeDataUrls[normalizeId(link._id)].dataUrl} 
                              alt={`QR Code for ${link.name}`}
                              style={{
                                width: '150px',
                                height: '150px',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                            
                            {/* QR Code Actions */}
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              gap: 1, 
                              mt: 2 
                            }}>
                              <Tooltip title="Download QR Code">
                                <IconButton 
                                  onClick={() => {
                                    const cleanId = normalizeId(link._id);
                                    handleDownloadQRCode(cleanId, link.name);
                                  }} 
                                  color="primary"
                                  size="small"
                                >
                                  <GetAppIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Customize QR Design">
                                <IconButton 
                                  onClick={() => {
                                    const cleanId = normalizeId(link._id);
                                    handleOpenQRCustomizer(cleanId, link.name);
                                  }} 
                                  color="secondary"
                                  size="small"
                                >
                                  <PaletteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Back to Main Page Button */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleBackToMain}
          style={{ minWidth: '200px' }}
        >
          Back to Main Page
        </Button>
      </div>

      <Dialog open={editDialogOpen} onClose={() => {
        logger.info('User closed edit dialog', {
          operation: 'editDialogClose',
          linkId: editLinkId,
          timestamp: new Date().toISOString()
        });
        
        // Track dialog close event
        appInsights.trackEvent('EditLinkDialogClosed', {
          component: 'AddLinksPage',
          source: 'frontend',
          operation: 'editDialogClose',
          linkId: editLinkId
        });
        
        setEditDialogOpen(false);
      }}>
        <DialogTitle>Edit Link</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To edit this link, please modify the name and URL fields below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            error={!!urlError}
            helperText={urlError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            logger.info('User cancelled edit dialog', {
              operation: 'editDialogCancel',
              linkId: editLinkId,
              timestamp: new Date().toISOString()
            });
            
            // Track cancel event
            appInsights.trackEvent('EditLinkDialogCancelled', {
              component: 'AddLinksPage',
              source: 'frontend',
              operation: 'editDialogCancel',
              linkId: editLinkId
            });
            
            setEditDialogOpen(false);
          }} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdateLink} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Customizer */}
      <QRCodeCustomizer
        open={qrCustomizerOpen}
        onClose={() => setQRCustomizerOpen(false)}
        linkId={customizingLinkId}
        linkName={customizingLinkName}
        onCustomQRGenerated={handleCustomQRGenerated}
      />
    </div>
  );
};

export default AddLinksPage;
